/**
 * Página: /admin/importar — Importador masivo desde Excel (Iter 56)
 *
 * Flujo:
 *  1. Drag&drop del .xlsx
 *  2. Vista de revisión fila-a-fila con badges de color (nuevo/match_exacto/
 *     match_dudoso/duplicado/aviso)
 *  3. Selector de cliente y espacio cuando hay candidatos dudosos
 *  4. Botón "Importar N filas" con confirmación
 *  5. Resumen post-importación + botón "Deshacer" (rollback)
 */
import React, { useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Copy, Sparkles, ArrowLeft, RefreshCw, Undo2, Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";


/** Normalizador: quita acentos, lowercase, trim — para buscar tolerante. */
function _norm(s) {
  return (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

/** Combobox buscable. Útil para clientes y espacios con muchas opciones.
 *  - options: array de {id, label, sublabel} (las creadas por sugerencia van etiquetadas con score)
 *  - value: id seleccionado o `__nuevo__:Nombre` para crear nuevo
 *  - newLabel: texto a usar para "Crear nuevo: 'X'"
 *  - placeholder: texto cuando no hay valor
 */
function SearchCombobox({ options = [], value, onChange, newName, placeholder = "Selecciona…", testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => o.id === value);
  const isNew = value === "__nuevo__";

  // Filtro manual: si el usuario escribe, filtra por label + sublabel; si no
  // coincide ninguno O el input está vacío, mostramos TODOS (lo que el usuario
  // pidió: poder navegar todo el catálogo aunque el matcher automático falle).
  const q = _norm(query);
  let visibles = options;
  if (q) {
    const matched = options.filter((o) => _norm(o.label).includes(q) || _norm(o.sublabel).includes(q));
    // Solo aplicamos el filtro si encontramos algo. Si 0 → caemos en "mostrar todos".
    visibles = matched.length > 0 ? matched : options;
  }

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="flex items-center justify-between w-full bg-zinc-950 border border-zinc-800 rounded h-7 px-2 text-[11px] text-left text-zinc-200 hover:bg-zinc-900">
          <span className="truncate flex-1">
            {selected ? (
              <span>
                {selected.score ? <span className="text-emerald-400 mr-1">✓</span> : null}
                {selected.label}
                {selected.sublabel ? <span className="text-zinc-500 ml-1">· {selected.sublabel}</span> : null}
                {selected.score ? <span className="text-zinc-500 ml-1">({Math.round(selected.score * 100)}%)</span> : null}
              </span>
            ) : isNew ? (
              <span className="text-emerald-300">+ Crear "{newName}"</span>
            ) : (
              <span className="text-zinc-500">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="w-3 h-3 text-zinc-500 ml-1 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-zinc-950 border-zinc-800 p-0 w-[320px]" align="start">
        {/* shouldFilter={false} → desactiva el matcher interno de cmdk (que era
            demasiado estricto y ocultaba clientes válidos). Filtramos a mano. */}
        <Command className="bg-zinc-950" shouldFilter={false}>
          <CommandInput placeholder="Buscar…  (vacío = lista completa)"
            value={query} onValueChange={setQuery}
            className="text-zinc-100 h-8 text-xs" />
          <CommandList className="max-h-72 overflow-y-auto">
            {visibles.length === 0 && (
              <CommandEmpty className="text-zinc-500 p-3 text-xs">Sin resultados</CommandEmpty>
            )}
            {newName && (
              <CommandGroup heading="Crear">
                <CommandItem value={`__nuevo__ ${newName}`}
                  onSelect={() => { onChange("__nuevo__"); setOpen(false); }}
                  className="text-emerald-300 cursor-pointer">
                  + Crear "{newName}"
                </CommandItem>
              </CommandGroup>
            )}
            {visibles.length > 0 && (
              <CommandGroup heading={
                q && visibles.length === options.length
                  ? `Sin coincidencia exacta — mostrando los ${options.length}`
                  : `${visibles.length} ${visibles.length === options.length ? "disponibles" : `de ${options.length}`}`
              }>
                {visibles.map((o) => (
                  <CommandItem key={o.id}
                    value={`${o.label} ${o.sublabel || ""} ${o.id}`}
                    onSelect={() => { onChange(o.id); setOpen(false); setQuery(""); }}
                    className="cursor-pointer text-zinc-200">
                    <Check className={`mr-2 h-3 w-3 ${value === o.id ? "opacity-100 text-emerald-400" : "opacity-0"}`} />
                    <span className="truncate flex-1">{o.label}</span>
                    {o.sublabel && <span className="text-zinc-500 text-[10px] ml-1">· {o.sublabel}</span>}
                    {o.score ? <span className="text-amber-300 text-[10px] ml-1">{Math.round(o.score * 100)}%</span> : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const ESTADO_LABEL = {
  nuevo: { label: "Nuevo", icon: Sparkles, cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  match_exacto: { label: "Match exacto", icon: CheckCircle2, cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  match_dudoso: { label: "Match dudoso", icon: AlertTriangle, cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  duplicado: { label: "Duplicado", icon: Copy, cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  aviso: { label: "Aviso", icon: AlertTriangle, cls: "bg-orange-500/15 text-orange-300 border-orange-500/30" },
};

export default function AdminImportar() {
  const [paso, setPaso] = useState(1);  // 1=upload, 2=review, 3=done
  const [analyze, setAnalyze] = useState(null);
  const [decisiones, setDecisiones] = useState({});  // { row: {incluir, cliente_id_final, espacio_id_final, ...} }
  const [resultado, setResultado] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [loading, setLoading] = useState(false);
  // Catálogos completos (para que los desplegables muestren TODO + permitan buscar)
  const [allClientes, setAllClientes] = useState([]);
  const [allEspacios, setAllEspacios] = useState([]);

  const subir = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Hacemos en paralelo: analyze + cargar todos los clientes + todos los espacios
      const [resAnalyze, resCli, resEsp] = await Promise.all([
        api.post("/admin/import/analyze", fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
        }),
        api.get("/clientes"),
        api.get("/espacios"),
      ]);
      const data = resAnalyze.data;
      setAllClientes((resCli.data || []).sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")));
      setAllEspacios((resEsp.data || []).sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")));
      setAnalyze(data);
      // Inicializa decisiones: si match_exacto o nuevo → incluir por defecto
      // si match_dudoso → incluir pero requiere elección; si duplicado → NO incluir
      const dec = {};
      for (const f of data.filas || []) {
        dec[f.row] = {
          incluir: f.estado_fila !== "duplicado",
          cliente_id_final: f.cliente_match?.id || null,
          cliente_nombre_nuevo: f.cliente_match ? null : (f.cliente || null),
          espacio_id_final: f.espacio_match?.id || null,
          espacio_nombre_nuevo: f.espacio_match ? null : (f.espacio || null),
          espacio_ciudad: f.poblacion || null,
          espacio_provincia: f.provincia || null,
          // Resto se hereda de la fila al hacer commit
          ...f,
        };
      }
      setDecisiones(dec);
      setPaso(2);
      toast.success(`${data.total_filas} filas analizadas`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al analizar");
    } finally { setLoading(false); }
  };

  const commit = async () => {
    const filas = Object.values(decisiones);
    const incluidas = filas.filter((d) => d.incluir);
    if (incluidas.length === 0) return toast.error("Sin filas a importar");
    if (!window.confirm(`¿Crear ${incluidas.length} peticiones? Esta acción es reversible con el botón Deshacer.`)) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/import/commit", { filas: incluidas }, { timeout: 180000 });
      setResultado(data);
      setPaso(3);
      toast.success(`Importadas ${data.resumen.peticiones} peticiones`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al importar");
    } finally { setLoading(false); }
  };

  const rollback = async () => {
    if (!resultado?.import_id) return;
    if (!window.confirm("¿Deshacer la importación? Se borrarán TODOS los registros creados (peticiones, líneas, componentes, clientes y espacios nuevos).")) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/import/${resultado.import_id}/rollback`);
      toast.success(`Deshecho: ${JSON.stringify(data.rolled_back)}`);
      setPaso(1); setAnalyze(null); setDecisiones({}); setResultado(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al deshacer");
    } finally { setLoading(false); }
  };

  const filasFiltradas = useMemo(() => {
    const arr = Object.values(decisiones);
    if (filtroEstado === "todos") return arr;
    if (filtroEstado === "incluidas") return arr.filter((f) => f.incluir);
    if (filtroEstado === "excluidas") return arr.filter((f) => !f.incluir);
    return arr.filter((f) => f.estado_fila === filtroEstado);
  }, [decisiones, filtroEstado]);

  const incluidas = useMemo(() => Object.values(decisiones).filter((d) => d.incluir).length, [decisiones]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Importar desde Excel"
        subtitle="Carga el archivo .xlsx, revisa fila a fila y confirma la importación"
        right={<Button variant="outline" onClick={() => window.history.back()}
          className="bg-transparent border-zinc-700 text-zinc-300"><ArrowLeft className="w-4 h-4 mr-2" /> Volver</Button>}
      />

      {/* PASO 1: Upload */}
      {paso === 1 && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-8 text-center" data-testid="paso-upload">
          <FileSpreadsheet className="w-16 h-16 text-emerald-400 mx-auto mb-3" />
          <h3 className="text-zinc-100 text-lg font-semibold mb-1">Carga tu archivo .xlsx</h3>
          <p className="text-zinc-500 text-sm mb-4">
            Debe tener una hoja llamada <code className="text-amber-300">Materiales</code> con columnas: ID, Fecha_Entrada, Fecha Montaje, Material/Montaje, Base, altura, m², Precio, Venta, Marca, Espacio, Población, Ciudad, Cliente, Pedido, Estado, Observaciones, Importe Montaje.
          </p>
          <Input type="file" accept=".xlsx" onChange={(e) => subir(e.target.files?.[0])}
            disabled={loading}
            data-testid="importar-file-input"
            className="bg-zinc-950 border-zinc-800 max-w-md mx-auto" />
          {loading && <div className="mt-3 text-amber-300 text-sm"><RefreshCw className="w-4 h-4 inline mr-1 animate-spin" /> Analizando…</div>}
        </div>
      )}

      {/* PASO 2: Review */}
      {paso === 2 && analyze && (
        <>
          {/* Resumen + acciones */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-zinc-100 text-sm">
                <b>{analyze.total_filas}</b> filas analizadas · <b className="text-amber-300">{incluidas}</b> seleccionadas
              </div>
              <div className="flex-1" />
              <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 w-44 h-9 text-xs" data-testid="filter-estado">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="incluidas">Solo incluidas</SelectItem>
                  <SelectItem value="excluidas">Solo excluidas</SelectItem>
                  <SelectItem value="nuevo">Nuevos</SelectItem>
                  <SelectItem value="match_exacto">Match exacto</SelectItem>
                  <SelectItem value="match_dudoso">Match dudoso</SelectItem>
                  <SelectItem value="duplicado">Duplicados</SelectItem>
                  <SelectItem value="aviso">Con avisos</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={commit} disabled={loading || incluidas === 0}
                className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400"
                data-testid="btn-importar-commit">
                <Upload className="w-4 h-4 mr-2" /> Importar {incluidas} filas
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
              {Object.entries(analyze.resumen).map(([k, v]) => {
                const cfg = ESTADO_LABEL[k];
                if (!cfg || !v) return null;
                return <span key={k} className={`px-2 py-0.5 rounded border ${cfg.cls}`}>{cfg.label}: {v}</span>;
              })}
            </div>
          </div>

          {/* Tabla de filas */}
          <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto max-h-[68vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-950 sticky top-0 z-10 text-[10px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-left">Estado</th>
                    <th className="p-2 text-left">Fila</th>
                    <th className="p-2 text-left">Descripción</th>
                    <th className="p-2 text-left min-w-[180px]">Cliente</th>
                    <th className="p-2 text-left min-w-[180px]">Espacio</th>
                    <th className="p-2 text-left">Fecha montaje</th>
                    <th className="p-2 text-right">€ Montaje</th>
                    <th className="p-2 text-left">Tipos</th>
                  </tr>
                </thead>
                <tbody>
                  {filasFiltradas.map((f) => (
                    <FilaImportar key={f.row} fila={f}
                      onChange={(patch) => setDecisiones((d) => ({ ...d, [f.row]: { ...d[f.row], ...patch } }))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* PASO 3: Resultado + rollback */}
      {paso === 3 && resultado && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6" data-testid="paso-done">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mb-3" />
          <h3 className="text-zinc-100 text-lg font-semibold mb-1">Importación completada</h3>
          <p className="text-zinc-300 text-sm mb-3">Import job: <code className="text-amber-300">{resultado.import_id}</code></p>
          <ul className="text-zinc-200 space-y-0.5 text-sm">
            <li>✓ <b>{resultado.resumen.clientes}</b> clientes creados</li>
            <li>✓ <b>{resultado.resumen.espacios}</b> espacios creados</li>
            <li>✓ <b>{resultado.resumen.peticiones}</b> peticiones creadas</li>
            <li>✓ <b>{resultado.resumen.lineas}</b> líneas de trabajo</li>
            <li>✓ <b>{resultado.resumen.componentes}</b> componentes (producción)</li>
          </ul>
          {resultado.errores?.length > 0 && (
            <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs">
              <div className="text-red-300 font-semibold">⚠ {resultado.errores.length} errores:</div>
              <ul className="text-red-200 mt-1">
                {resultado.errores.slice(0, 10).map((e, i) => <li key={i}>Fila {e.row}: {e.error}</li>)}
              </ul>
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <Button onClick={() => { setPaso(1); setAnalyze(null); setResultado(null); setDecisiones({}); }}
              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100">
              Nueva importación
            </Button>
            <Button onClick={rollback} disabled={loading}
              variant="outline"
              className="bg-transparent border-red-500/40 text-red-300 hover:bg-red-500/10"
              data-testid="btn-rollback">
              <Undo2 className="w-4 h-4 mr-2" /> Deshacer importación
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function FilaImportar({ fila, allClientes, allEspacios, onChange }) {
  const cfg = ESTADO_LABEL[fila.estado_fila] || ESTADO_LABEL.aviso;
  const Icon = cfg.icon;
  const f = fila;

  // Opciones del combobox de cliente: primero los sugeridos (match exacto + dudosos)
  // luego TODO el resto del catálogo. Esto permite que el usuario busque por nombre
  // si la sugerencia automática no le convence.
  const sugeridosCliId = new Set();
  const opcionesCliente = useMemo(() => {
    const arr = [];
    if (f.cliente_match) {
      arr.push({ id: f.cliente_match.id, label: f.cliente_match.nombre, score: f.cliente_match.score });
      sugeridosCliId.add(f.cliente_match.id);
    }
    for (const c of f.cliente_candidatos || []) {
      arr.push({ id: c.id, label: c.nombre, sublabel: c.razon_social, score: c.score });
      sugeridosCliId.add(c.id);
    }
    // Resto del catálogo ordenado alfabéticamente
    const rest = (allClientes || [])
      .filter((c) => !sugeridosCliId.has(c.id))
      .map((c) => ({ id: c.id, label: c.nombre || c.razon_social || c.id.slice(0, 8) }))
      .sort((a, b) => a.label.localeCompare(b.label, "es"));
    arr.push(...rest);
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allClientes, f.cliente_match, f.cliente_candidatos]);

  const sugeridosEspId = new Set();
  const opcionesEspacio = useMemo(() => {
    const arr = [];
    if (f.espacio_match) {
      arr.push({ id: f.espacio_match.id, label: f.espacio_match.nombre,
        sublabel: f.espacio_match.ciudad, score: f.espacio_match.score });
      sugeridosEspId.add(f.espacio_match.id);
    }
    for (const e of f.espacio_candidatos || []) {
      arr.push({ id: e.id, label: e.nombre, sublabel: `${e.ciudad || ""}${e.provincia ? " · " + e.provincia : ""}`.trim(), score: e.score });
      sugeridosEspId.add(e.id);
    }
    const restE = (allEspacios || [])
      .filter((e) => !sugeridosEspId.has(e.id))
      .map((e) => ({
        id: e.id,
        label: e.nombre || e.id.slice(0, 8),
        sublabel: `${e.ciudad || ""}${e.provincia ? " · " + e.provincia : ""}`.trim(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "es"));
    arr.push(...restE);
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEspacios, f.espacio_match, f.espacio_candidatos]);

  // Valor actual del combobox (id, "__nuevo__" o null)
  const cliValue = f.cliente_id_final || (f.cliente_nombre_nuevo ? "__nuevo__" : null);
  const espValue = f.espacio_id_final || (f.espacio_nombre_nuevo ? "__nuevo__" : null);

  return (
    <tr className={`border-b border-zinc-800/60 hover:bg-zinc-900/40 ${!f.incluir ? "opacity-40" : ""}`}>
      <td className="p-2 text-center">
        <input type="checkbox" checked={!!f.incluir}
          onChange={(e) => onChange({ incluir: e.target.checked })}
          className="accent-emerald-500"
          data-testid={`fila-incluir-${f.row}`} />
      </td>
      <td className="p-2">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] ${cfg.cls}`}>
          <Icon className="w-2.5 h-2.5" /> {cfg.label}
        </span>
        {f.avisos?.length > 0 && (
          <div className="text-[9px] text-orange-300 mt-0.5">{f.avisos.join(" · ")}</div>
        )}
      </td>
      <td className="p-2 text-zinc-500 font-mono text-[10px]">#{f.row}</td>
      <td className="p-2 text-zinc-200 max-w-[260px]">
        <div className="truncate" title={f.descripcion}>{f.descripcion || <em className="text-zinc-600">(sin descripción)</em>}</div>
        {f.id_externo && <div className="text-[9px] text-zinc-600">ID Excel: {f.id_externo}</div>}
      </td>
      {/* Cliente */}
      <td className="p-2 min-w-[200px]">
        <SearchCombobox
          options={opcionesCliente}
          value={cliValue}
          newName={f.cliente}
          placeholder="Sin cliente"
          testId={`fila-cli-${f.row}`}
          onChange={(v) => onChange({
            cliente_id_final: v === "__nuevo__" ? null : v,
            cliente_nombre_nuevo: v === "__nuevo__" ? f.cliente : null,
          })}
        />
      </td>
      {/* Espacio */}
      <td className="p-2 min-w-[220px]">
        <SearchCombobox
          options={opcionesEspacio}
          value={espValue}
          newName={f.espacio}
          placeholder="Sin espacio"
          testId={`fila-esp-${f.row}`}
          onChange={(v) => onChange({
            espacio_id_final: v === "__nuevo__" ? null : v,
            espacio_nombre_nuevo: v === "__nuevo__" ? f.espacio : null,
          })}
        />
        {(f.poblacion || f.provincia) && espValue === "__nuevo__" && (
          <div className="text-zinc-500 text-[9px] mt-0.5">→ {f.poblacion} · {f.provincia}</div>
        )}
      </td>
      <td className="p-2 text-zinc-400 font-mono text-[10px]">{f.fecha_montaje || "—"}</td>
      <td className="p-2 text-right text-emerald-300 font-mono">{f.importe_montaje ? `${f.importe_montaje}€` : "—"}</td>
      <td className="p-2 text-zinc-500 text-[10px]">{f.tipos_linea?.join(", ") || "—"}</td>
    </tr>
  );
}
